import {
  estimateWorkspaceViewSize,
  isWithinWorkspaceViewCodePointLimit,
  parseWorkspaceViewSnapshot,
  type UiObjectRef,
  type UiSemanticElement,
  WORKSPACE_VIEW_ESTIMATED_BYTES_PER_TOKEN,
  WORKSPACE_VIEW_MAX_BYTES,
  WORKSPACE_VIEW_MAX_ESTIMATED_TOKENS,
  WORKSPACE_VIEW_MAX_INTERACTIONS,
  WORKSPACE_VIEW_MAX_NEIGHBOURS,
  WORKSPACE_VIEW_MAX_REF_VISITS,
  WORKSPACE_VIEW_MAX_ROUTE_CODE_POINTS,
  WORKSPACE_VIEW_MAX_TITLE_CODE_POINTS,
  type WorkspaceViewReadResult,
  type WorkspaceViewSnapshot,
} from '@streamlain/copilot-realtime-contract';

import { admitWorkspaceViewSnapshot } from './workspace-view-raw-admission';

/** Shortens long view text deterministically by code points instead of rejecting the view. */
export function fitWorkspaceViewText(
  value: string,
  max: number
): { value: string; truncated: boolean } {
  if (isWithinWorkspaceViewCodePointLimit(value, max))
    return { value, truncated: false };
  let kept = '',
    count = 0;
  for (const point of value) {
    if (++count >= max) break;
    kept += point;
  }
  return { value: kept + '…', truncated: true };
}

/**
 * What stays of an element's label and text while a view is cut to size, in
 * bytes: an element without its name cannot be told from its neighbours, so a
 * view that is too large loses elements from its end before it loses names.
 */
const ELEMENT_TEXT_FLOOR_BYTES = 32;

/** Admit native references before materializing a whole model selection. */
export class WorkspaceObjectRefBudget {
  private readonly selected = new Map<string, UiObjectRef>();
  private readonly sizes = new Map<string, number>();
  private visits = 0;
  private bytes: number;
  private readonly limit = Math.min(
    WORKSPACE_VIEW_MAX_BYTES,
    WORKSPACE_VIEW_MAX_ESTIMATED_TOKENS *
      WORKSPACE_VIEW_ESTIMATED_BYTES_PER_TOKEN
  );
  truncated = false;

  constructor(private readonly primary: UiObjectRef) {
    const bytes = this.size(primary);
    if (bytes === null || bytes + 2 > this.limit)
      throw new Error('Primary workspace reference unavailable');
    this.bytes = bytes + 2;
  }

  /** Charge before reading a model entry, including an empty selection. */
  visit(): boolean {
    if (this.truncated || this.visits >= WORKSPACE_VIEW_MAX_REF_VISITS) {
      this.truncated = true;
      return false;
    }
    this.visits++;
    return true;
  }

  add(ref: UiObjectRef): boolean {
    if (this.truncated) return false;
    const size = this.size(ref);
    if (size === null) {
      this.truncated = true;
      return false;
    }
    const previous = this.sizes.get(ref.id);
    const delta = size - (previous ?? -1);
    if (this.bytes + delta > this.limit) {
      this.truncated = true;
      return false;
    }
    this.bytes += delta;
    this.sizes.set(ref.id, size);
    this.selected.set(ref.id, ref);
    return true;
  }

  get refs(): readonly UiObjectRef[] {
    return [this.primary, ...this.selected.values()];
  }

  private size(ref: UiObjectRef): number | null {
    // Length checks are constant time. Never encode a giant model string first.
    let units = ref.kind.length + ref.id.length + (ref.docId?.length ?? 0);
    if (units > this.limit) return null;
    let fields = 0;
    for (const key in ref.attributes) {
      if (!Object.hasOwn(ref.attributes, key)) continue;
      if (++fields > WORKSPACE_VIEW_MAX_REF_VISITS) return null;
      const value = ref.attributes[key];
      units += key.length + (typeof value === 'string' ? value.length : 0);
      if (
        units > this.limit ||
        (value !== null &&
          !['string', 'number', 'boolean'].includes(typeof value))
      )
        return null;
    }
    return estimateWorkspaceViewSize(ref)?.bytes ?? null;
  }
}

/** Deterministic projection for the UI envelope, separate from domain attachments. */
export function fitWorkspaceViewSnapshot(
  snapshot: WorkspaceViewSnapshot
): WorkspaceViewReadResult {
  const scope = { workspaceId: snapshot.workspaceId, viewId: snapshot.viewId };
  const fail = (reason: 'oversized' | 'invalid'): WorkspaceViewReadResult => ({
    ok: false,
    reason,
    scope,
  });
  try {
    if (
      typeof snapshot.title !== 'string' ||
      typeof snapshot.route !== 'string'
    )
      return fail('invalid');
    // Producers fit route/title via fitWorkspaceViewText; unfitted binding text is invalid input.
    if (
      !isWithinWorkspaceViewCodePointLimit(
        snapshot.title,
        WORKSPACE_VIEW_MAX_TITLE_CODE_POINTS
      ) ||
      !isWithinWorkspaceViewCodePointLimit(
        snapshot.route,
        WORKSPACE_VIEW_MAX_ROUTE_CODE_POINTS
      )
    )
      return fail('oversized');
    const admitted = admitWorkspaceViewSnapshot(snapshot);
    if (!admitted.ok) return fail(admitted.reason);
    const value = admitted.value;
    // Count the normalized envelope once; subsequent edits adjust exact JSON bytes.
    // Individual items/strings are serialized, never the whole snapshot per edit.
    const byteLimit = Math.min(
      WORKSPACE_VIEW_MAX_BYTES,
      WORKSPACE_VIEW_MAX_ESTIMATED_TOKENS *
        WORKSPACE_VIEW_ESTIMATED_BYTES_PER_TOKEN
    );
    const bytesOf = (entry: unknown) => {
      const size = estimateWorkspaceViewSize(entry);
      if (!size) throw new Error('Invalid context item');
      return size.bytes;
    };
    let bytes = 0;
    const fits = () => bytes <= byteLimit;
    const trim = () => {
      if (!value.truncated) {
        bytes += bytesOf(true) - bytesOf(false);
        value.truncated = true;
      }
    };
    if (value.interactions.length > WORKSPACE_VIEW_MAX_INTERACTIONS) {
      value.interactions = value.interactions.slice(
        -WORKSPACE_VIEW_MAX_INTERACTIONS
      );
      trim();
    }
    for (const item of value.interactions) {
      if (item.neighbours.length > WORKSPACE_VIEW_MAX_NEIGHBOURS) {
        item.neighbours = item.neighbours.slice(
          0,
          WORKSPACE_VIEW_MAX_NEIGHBOURS
        );
        trim();
      }
    }
    bytes = bytesOf(value);
    while (!fits() && value.interactions.length) {
      bytes -=
        bytesOf(value.interactions[0]) +
        (value.interactions.length > 1 ? 1 : 0);
      value.interactions = value.interactions.slice(1);
      trim();
    }
    // What the user sees outranks the automatic navigation offer: search keeps
    // the complete catalogue reachable, so the offer goes before any element.
    // Dropping offered destinations loses nothing visible: the view is not truncated.
    const removed = new Set<number>();
    for (let i = value.actions.length - 1; !fits() && i >= 0; i--) {
      if (value.actions[i].capability === 'workspace-search') continue;
      bytes -=
        bytesOf(value.actions[i]) +
        (value.actions.length - removed.size > 1 ? 1 : 0);
      removed.add(i);
    }
    if (removed.size)
      value.actions = value.actions.filter((_, i) => !removed.has(i));
    // Stable max heap: summary, then DOM order, text before label for equal lengths.
    // structuredClone preserves aliases: editing one shared element changes every
    // occurrence in elements/interactions, and every byte delta must count those.
    const occurrences = new Map<UiSemanticElement, number>();
    const count = (entry: UiSemanticElement) =>
      occurrences.set(entry, (occurrences.get(entry) ?? 0) + 1);
    value.elements.forEach(count);
    value.interactions.forEach(item => {
      count(item.target);
      item.neighbours.forEach(count);
    });
    const encoder = new TextEncoder();
    type TextEntry = {
      read: () => string;
      write: (text: string) => void;
      length: number;
      order: number;
      occurrences: number;
      floor: number;
    };
    const heap: TextEntry[] = [];
    const add = (
      read: () => string,
      write: (text: string) => void,
      weight: number,
      floor: number
    ) => {
      heap.push({
        read,
        write,
        length: encoder.encode(read()).length,
        order: heap.length,
        occurrences: weight,
        floor,
      });
    };
    add(
      () => value.summary,
      text => {
        value.summary = text;
      },
      1,
      0
    );
    const seen = new Set<UiSemanticElement>();
    for (const entry of value.elements) {
      if (seen.has(entry)) continue;
      seen.add(entry);
      for (const field of ['text', 'label'] as const) {
        // An element without text of its own has none to shorten.
        if (entry[field] === undefined) continue;
        add(
          () => entry[field] ?? '',
          text => {
            entry[field] = text;
          },
          occurrences.get(entry)!,
          ELEMENT_TEXT_FLOOR_BYTES
        );
      }
    }
    // What an entry can still give: nothing once it is down to its floor.
    const spare = (entry: TextEntry) =>
      entry.length > entry.floor ? entry.length : 0;
    const before = (a: TextEntry, b: TextEntry) =>
      spare(a) > spare(b) || (spare(a) === spare(b) && a.order < b.order);
    const sift = (start: number) => {
      let index = start;
      for (;;) {
        const left = 2 * index + 1,
          right = left + 1;
        let next = index;
        if (left < heap.length && before(heap[left], heap[next])) next = left;
        if (right < heap.length && before(heap[right], heap[next]))
          next = right;
        if (next === index) return;
        [heap[index], heap[next]] = [heap[next], heap[index]];
        index = next;
      }
    };
    for (let i = Math.floor(heap.length / 2) - 1; i >= 0; i--) sift(i);
    while (!fits() && heap[0] && spare(heap[0])) {
      const longest = heap[0],
        previous = longest.read();
      const points = Array.from(previous);
      const text = points.slice(0, Math.floor(points.length / 2)).join('');
      bytes += (bytesOf(text) - bytesOf(previous)) * longest.occurrences;
      longest.write(text);
      longest.length = encoder.encode(text).length;
      trim();
      sift(0);
    }
    // Remove a suffix once, avoiding repeated copies of growing arrays.
    const shorten = <T>(items: readonly T[], minimum: number): readonly T[] => {
      let remaining = items.length;
      while (!fits() && remaining > minimum) {
        bytes -= bytesOf(items[remaining - 1]) + (remaining > 1 ? 1 : 0);
        remaining--;
        trim();
      }
      return remaining === items.length ? items : items.slice(0, remaining);
    };
    value.elements = shorten(value.elements, 0);
    if (!fits()) {
      // No element is left: what recent interactions still name gives way last.
      for (const item of value.interactions)
        for (const entry of [item.target, ...item.neighbours]) {
          entry.label = '';
          delete entry.text;
        }
      bytes = bytesOf(value);
    }
    // The first ref is the real primary object binding delivered by its owner.
    value.refs = shorten(value.refs, 1);
    if (!fits()) return fail('oversized');
    const parsed = parseWorkspaceViewSnapshot(value, scope.workspaceId);
    return parsed.ok ? parsed : { ok: false, reason: parsed.reason, scope };
  } catch {
    return fail('invalid');
  }
}
